import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../utils/api"; // custom axios instance
import {
  Layout,
  Button,
  Typography,
  Form,
  Input,
  Select,
  Col,
  Row,
  Switch,
  message,
  Modal,
  Spin,
  Progress,
} from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import imgSignIn from "../assets/images/img-signin.jpg";
import imgSignInMatlosana from "../assets/images/img-signin-appverse.jpg";
import { Header, Footer } from "components/Layout";
import { API_CONFIG } from "./config";
import { useAuth } from "../AuthContext";

// If REACT_APP_SIGNIN_IMAGE is set, use the standard sign-in image; otherwise default to Matlosana
const ENV_SIGNIN_IMAGE = process.env.REACT_APP_SIGNIN_IMAGE;
const signinbg = ENV_SIGNIN_IMAGE ? imgSignIn : imgSignInMatlosana;

const { Content } = Layout;
const { Title } = Typography;
const { Option } = Select;

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // State for modal and new password
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [currentUsername, setCurrentUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [newPwdStrength, setNewPwdStrength] = useState(0);
  const [tenants, setTenants] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const loadTenants = async () => {
      setTenantLoading(true);
      try {
        const response = await api.post(API_CONFIG.tenantsListUrl, {});
        const list = response?.data?.tenants || [];
        setTenants(list);
        const savedTenant = localStorage.getItem("tenantId") || localStorage.getItem("mockdb:tenantId");
        const initial = savedTenant ? Number(savedTenant) : list[0]?.id;
        if (initial) form.setFieldsValue({ tenantId: initial });
      } catch {
        setTenants([]);
      } finally {
        setTenantLoading(false);
      }
    };
    loadTenants();
  }, []);

  // Strong password rules
  const passwordRules = [
    { pattern: /.{8,}/, message: "Password must be at least 8 characters long." },
    { pattern: /[A-Z]/, message: "Password must include at least one uppercase letter." },
    { pattern: /[a-z]/, message: "Password must include at least one lowercase letter." },
    { pattern: /[0-9]/, message: "Password must include at least one number." },
    { pattern: /[@$!%*?&#]/, message: "Password must include at least one special character." },
  ];

  const validatePassword = (password) => {
    for (const rule of passwordRules) {
      if (!rule.pattern.test(password)) {
        return rule.message;
      }
    }
    return null; // Valid password
  };

  const getPasswordStrength = (pwd) => {
    let score = 0;
    if (/.{8,}/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[@$!%*?&#]/.test(pwd)) score++;
    return Math.round((score / passwordRules.length) * 100);
  };

  const handlePasswordChange = async () => {
    const validationError = validatePassword(newPassword);
    if (validationError) {
      message.error(validationError);
      return;
    }

    try {
      const response = await api.post(API_CONFIG.changePasswordUrl, {
        username: currentUsername,
        newPassword,
      });


      if (!response?.data) throw new Error("Unexpected response from server.");

      message.success("Password changed successfully. Please log in again.");
      setIsModalVisible(false); // Close the modal
    } catch (error) {
      message.error(error.message || "Failed to update the password. Please try again.");
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await api.post(API_CONFIG.loginUrl, {
        username: values.username,
        password: values.password,
        tenantId: values.tenantId,
      });

      if (!response?.data) throw new Error("Unexpected response from server.");

      const data = response.data;

      if (data.ErrorCode && data.ErrorCode !== "SUCCESS") {
        switch (data.ErrorCode) {
          case "INVALID_CREDENTIALS":
            throw new Error("Invalid username or password. Please try again.");
          case "ACCOUNT_INACTIVE":
            throw new Error("Your account is inactive. Contact support.");
          case "ACCOUNT_LOCKED":
            throw new Error("Your account is locked. Please try again later or contact support.");
          case "PASSWORD_CHANGE_REQUIRED":
            message.info("You need to change your password before proceeding.");
            setCurrentUsername(values.username); // Save the username for password change
            setIsModalVisible(true); // Show the password change modal
            return;
          case "TENANT_ACCESS_DENIED":
            throw new Error("You do not have access to this tenant.");
          default:
            throw new Error("An unknown error occurred. Please try again.");
        }
      }

      message.success("Login successful!");

      localStorage.setItem("token", data.token);
      if (data.TenantId) {
        localStorage.setItem("tenantId", String(data.TenantId));
        localStorage.setItem("mockdb:tenantId", String(data.TenantId));
      }

      login(data); // Save user details to context
      const params = new URLSearchParams(location.search);
      const redirect = params.get("redirect");
      const safeRedirect = redirect && redirect.startsWith("/") ? redirect : null;
      navigate(safeRedirect || (data.RoleId === 5 ? "/admin" : "/search"));
    } catch (error) {
      const serverErrorCode = error?.response?.data?.ErrorCode;
      const status = error?.response?.status;

      if (serverErrorCode === "PASSWORD_CHANGE_REQUIRED") {
        message.info("You need to change your password before proceeding.");
        setCurrentUsername(values.username);
        setIsModalVisible(true);
        return;
      }
      if (serverErrorCode === "TENANT_ACCESS_DENIED") {
        message.error("You do not have access to this tenant.");
        return;
      }
      if (serverErrorCode === "ACCOUNT_LOCKED") {
        message.error("Your account is locked. Please contact support.");
        return;
      }

      if (status === 401) {
        message.error("Invalid username or password. Please try again.");
      } else if (status === 403) {
        message.error("Access denied. You do not have permission to access this resource.");
      } else if (status === 500) {
        message.error("A server error occurred. Please try again later.");
      } else {
        message.error(error.message || "Login failed. Please try again.");
      }

      console.error("Login Error: ", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="layout-default layout-signin">
      <Header btn="primary" />
      <Content className="signin">
        <Row gutter={[24, 0]} justify="space-around">
          <Col xs={{ span: 24 }} lg={{ span: 6 }} md={{ span: 12 }}>
            <div className="signin-card">
              <Title className="mb-15 gradient-head">Sign In</Title>
              <Title className="font-regular text-muted" level={5}>
                Enter your username and password to sign in
              </Title>
              <Spin spinning={loading} tip="Signing in...">
                <Form form={form} onFinish={onFinish} layout="vertical" className="row-col">
                  <Form.Item
                    className="tenant"
                    label="Tenant"
                    name="tenantId"
                    rules={[{ required: true, message: "Select a tenant" }]}
                  >
                    <Select placeholder="Select tenant" loading={tenantLoading}>
                      {tenants.map((tenant) => (
                        <Option key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    className="username"
                    label="Username"
                    name="username"
                    rules={[{ required: true, message: "Please input your username!" }]}
                  >
                    <Input placeholder="Username" autoComplete="username" prefix={<UserOutlined />} />
                  </Form.Item>
                  <Form.Item
                    className="password"
                    label="Password"
                    name="password"
                    rules={[{ required: true, message: "Please input your password!" }]}
                  >
                    <Input.Password
                      placeholder="Password"
                      autoComplete="current-password"
                      prefix={<LockOutlined />}
                      onKeyUp={(e) => setCapsLockOn(e.getModifierState && e.getModifierState('CapsLock'))}
                    />
                  </Form.Item>
                  {capsLockOn && (
                    <div className="caps-hint">Caps Lock is ON</div>
                  )}
                  <Form.Item name="remember" className="align-center" valuePropName="checked">
                    <Switch defaultChecked />
                    Remember me
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" style={{ width: "100%" }} loading={loading} disabled={loading}>
                      SIGN IN
                    </Button>
                  </Form.Item>
                </Form>
              </Spin>
            </div>
          </Col>
          <Col
            className="sign-img"
            style={{ padding: 12 }}
            xs={{ span: 24 }}
            lg={{ span: 12 }}
            md={{ span: 12 }}
          >
            <img src={signinbg} alt="" />
          </Col>
        </Row>
      </Content>
      <Footer />

      {/* Password Change Modal */}
      <Modal
        title="Change Password"
        visible={isModalVisible}
        onOk={handlePasswordChange}
        onCancel={() => setIsModalVisible(false)}
        okText="Change Password"
        cancelText="Cancel"
      >
        <p>Your password needs to be updated before proceeding. Please follow these rules:</p>
        <ul>
          {passwordRules.map((rule, index) => (
            <li key={index}>{rule.message}</li>
          ))}
        </ul>
        <Input.Password
          autoFocus
          placeholder="Enter new password"
          value={newPassword}
          autoComplete="new-password"
          onChange={(e) => {
            const v = e.target.value;
            setNewPassword(v);
            setNewPwdStrength(getPasswordStrength(v));
          }}
          onKeyUp={(e) => setCapsLockOn(e.getModifierState && e.getModifierState('CapsLock'))}
        />
        {capsLockOn && (
          <div className="caps-hint" style={{ marginTop: 8 }}>Caps Lock is ON</div>
        )}
        <div className="pwd-strength">
          <Progress percent={newPwdStrength} showInfo={false} strokeLinecap="round" />
        </div>
      </Modal>
      <style>{`
        /* --- Sign In page premium styling --- */
        .layout-signin .signin {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(1000px 480px at 12% 18%, rgba(201,171,99,0.12), transparent 62%),
            radial-gradient(1200px 700px at -10% 0%, rgba(201,171,99,0.06), transparent 70%),
            radial-gradient(1000px 600px at 50% 100%, rgba(124,212,255,0.06), transparent 70%);
          padding: 24px 0 8px;
        }
        /* Subtle vignette to enrich shading without hurting readability */
        .layout-signin .signin::before {
          content: "";
          position: absolute; inset: 0; pointer-events: none;
          background:
            radial-gradient(140% 80% at 50% -10%, rgba(255,255,255,0.03), transparent 60%),
            radial-gradient(120% 85% at 50% 110%, rgba(0,0,0,0.14), transparent 60%);
        }
        .layout-signin .signin .ant-row { align-items: stretch; }
        .layout-signin .signin .ant-col { display: flex; }
        .layout-signin .sign-img { position: relative; }
        .layout-signin .sign-img::after {
          content: "";
          position: absolute; inset: 0; pointer-events: none;
          background:
            radial-gradient(120% 60% at 20% 20%, rgba(201,171,99,0.22), transparent 60%),
            radial-gradient(120% 60% at 100% 0%, rgba(176,139,211,0.22), transparent 60%);
        }
        .layout-signin .sign-img { display: flex; }
        .layout-signin .sign-img img {
          width: 100%;
          height: 100%;
          max-height: none;
          object-fit: cover;
          border-radius: 16px;
          display: block;
        }

        .signin-card {
          position: relative;
          padding: 24px;
          border-radius: 16px;
          background:
            radial-gradient(100% 80% at 0% 0%, rgba(201,171,99,0.22), transparent 60%),
            radial-gradient(110% 70% at 100% 100%, rgba(176,139,211,0.20), transparent 60%),
            linear-gradient(180deg, rgba(11,18,32,0.78), rgba(12,16,24,0.78));
          border: 1px solid rgba(201,171,99,0.28);
          backdrop-filter: blur(8px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.04);
          overflow: hidden;
          margin-top: -6px;
          height: 100%;
          display: flex;
          flex-direction: column;
          isolation: isolate;
          transition: box-shadow 240ms ease, transform 240ms ease, filter 240ms ease;
        }
        .signin-card::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 16px;
          padding: 1px; /* border thickness */
          background: linear-gradient(120deg, #C9AB63, #B08BD3, #7CD4FF, #C9AB63);
          background-size: 300% 300%;
          animation: hueShift 10s linear infinite;
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          pointer-events: none;
          opacity: 0.9;
        }
        @keyframes hueShift { to { background-position: 300% 0; } }
        .signin-card:hover { transform: translateY(-2px); filter: saturate(1.04) brightness(1.02); box-shadow: 0 14px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05); }
        .signin-card .gradient-head {
          color: #F2E7C6; /* warm, high-contrast on dark */
          text-shadow: 0 2px 14px rgba(0,0,0,0.45), 0 0 1px rgba(0,0,0,0.60);
          letter-spacing: -0.01em;
        }
        @supports not (-webkit-background-clip: text) {
          .signin-card .gradient-head {
            background: none;
            -webkit-text-fill-color: initial;
            color: #F2E7C6; /* same readable fallback */
            text-shadow: 0 2px 14px rgba(0,0,0,0.45), 0 0 1px rgba(0,0,0,0.60);
          }
        }
        /* Fallback: disable masked rim if mask-composite is unsupported to avoid covering the title */
        @supports not ((-webkit-mask: linear-gradient(#000 0 0)) and (mask-composite: exclude)) {
          .signin-card::after { display: none; }
        }
        .signin-card::before {
          content: "";
          position: absolute; inset: -20% -40%;
          background: linear-gradient(120deg, rgba(255,255,255,0) 35%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0) 65%);
          transform: translateX(-120%) rotate(8deg);
          transition: transform 900ms ease;
          pointer-events: none; mix-blend-mode: screen;
        }
        .signin-card:hover::before { transform: translateX(120%) rotate(8deg); }

        /* Inputs */
        .layout-signin .ant-input,
        .layout-signin .ant-input-password,
        .layout-signin .ant-input-affix-wrapper {
          background: linear-gradient(180deg, rgba(15,23,42,0.70), rgba(11,18,32,0.70));
          border-color: rgba(148,163,184,0.22);
          color: #e6edf7;
        }
        .layout-signin .ant-input:focus,
        .layout-signin .ant-input-focused,
        .layout-signin .ant-input-password:focus-within,
        .layout-signin .ant-input-affix-wrapper:focus,
        .layout-signin .ant-input-affix-wrapper-focused,
        .layout-signin .ant-input-affix-wrapper:hover {
          border-color: rgba(59,130,246,0.65) !important;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.28), 0 10px 26px rgba(59,130,246,0.14);
        }
        .layout-signin input::placeholder { color: rgba(226,232,240,0.55); }
        .signin-card .ant-form-item-label > label { color: #E8D7A8; font-weight: 500; letter-spacing: 0.2px; }

        .layout-signin .ant-input-prefix, .layout-signin .ant-input-suffix { color: #9fb5ff; }

        /* Remember me label on dark background */
        .layout-signin .align-center { color: #E8D7A8; }

        /* Normalize input widths (fix mismatch on small screens) */
        .layout-signin .row-col .ant-form-item .ant-input,
        .layout-signin .row-col .ant-form-item .ant-input-affix-wrapper,
        .layout-signin .row-col .ant-form-item .ant-input-password {
          width: 100% !important;
          box-sizing: border-box;
        }
        .layout-signin .row-col .ant-form-item-control,
        .layout-signin .row-col .ant-form-item-control-input,
        .layout-signin .row-col .ant-form-item-control-input-content {
          width: 100%;
        }
        /* Ensure password input inherits the same min-height as other inputs */
        .layout-signin .ant-input-password .ant-input { min-height: inherit; }

        /* Primary Button with glossy sweep */
        .layout-signin .ant-btn-primary {
          position: relative;
          border: 1px solid rgba(96,165,250,0.95);
          background: linear-gradient(135deg, rgba(59,130,246,0.92), rgba(33,93,227,0.94));
          box-shadow: 0 10px 25px rgba(37,99,235,0.35), 0 1px 0 rgba(255,255,255,0.08) inset;
          overflow: hidden;
          transition: transform 200ms ease, filter 200ms ease, box-shadow 200ms ease;
        }
        .layout-signin .ant-btn-primary:hover {
          filter: saturate(1.08) brightness(1.05);
          transform: translateY(-1px);
          box-shadow: 0 14px 30px rgba(37,99,235,0.35), 0 1px 0 rgba(255,255,255,0.10) inset;
        }
        .layout-signin .ant-btn-primary::before {
          content: ""; position: absolute; inset: -30% -10%;
          background: linear-gradient(120deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0) 60%);
          transform: translateX(-120%) rotate(8deg);
          transition: transform 600ms ease; pointer-events: none; mix-blend-mode: screen;
          border-radius: inherit;
        }
        .layout-signin .ant-btn-primary:hover::before { transform: translateX(120%) rotate(8deg); }

        .caps-hint { margin-top: -8px; margin-bottom: 12px; font-size: 12px; color: #fca5a5; }
        .pwd-strength { margin-top: 12px; }

        /* --- Fluid typography using clamp() --- */
        .layout-signin {
          /* Design tokens for a fluid type scale */
          --step-0: clamp(0.875rem, 0.82rem + 0.30vw, 1.00rem); /* Base/body */
          --step-1: clamp(1.00rem, 0.94rem + 0.60vw, 1.25rem);  /* Small headings, buttons */
          --step-2: clamp(1.25rem, 1.05rem + 1.20vw, 1.75rem);  /* Section titles */
          --step-3: clamp(1.75rem, 1.35rem + 2.20vw, 2.50rem);  /* Page titles (md) */
          --step-4: clamp(2.125rem, 1.60rem + 3.00vw, 3.00rem); /* Page titles (lg) */
        }

        /* Apply the scale */
        .layout-signin .signin-card .gradient-head {
          font-size: var(--step-4);
          line-height: 1.1;
          letter-spacing: -0.01em;
        }
        .layout-signin .signin-card .font-regular.text-muted {
          font-size: var(--step-1);
          line-height: 1.35;
          color: #F2E7C6; /* warm light on dark glass for better legibility */
          opacity: 1; /* remove dimming */
          text-shadow: 0 1px 2px rgba(0,0,0,0.35); /* subtle lift for readability */
        }
        .layout-signin .ant-form-item-label > label {
          font-size: var(--step-0);
          line-height: 1.3;
        }
        .layout-signin .ant-input,
        .layout-signin .ant-input-password,
        .layout-signin .ant-input-affix-wrapper {
          font-size: var(--step-0);
        }
        .layout-signin .ant-btn-primary {
          font-size: var(--step-1);
          line-height: 1.2;
        }
        .layout-signin .caps-hint {
          font-size: clamp(0.75rem, 0.70rem + 0.20vw, 0.875rem);
          line-height: 1.2;
        }
        .layout-signin .pwd-strength .ant-progress-text {
          font-size: clamp(0.75rem, 0.70rem + 0.20vw, 0.875rem);
        }

        /* Make image caption or any small helper text scale nicely if added later */
        .layout-signin .helper-text { /* optional utility */
          font-size: clamp(0.8125rem, 0.76rem + 0.25vw, 0.9375rem);
          line-height: 1.35;
        }

        /* --- Mobile-first button & input sizing --- */
        .layout-signin .ant-btn,
        .layout-signin .ant-btn-primary {
          min-height: 44px; /* Mobile touch target */
          padding-block: 12px;
          padding-inline: 16px;
          border-radius: 12px;
        }
        .layout-signin .ant-btn-default {
          min-height: 44px;
          padding-block: 12px;
          padding-inline: 16px;
          border-radius: 12px;
        }
        .layout-signin .ant-input,
        .layout-signin .ant-input-password,
        .layout-signin .ant-input-affix-wrapper {
          min-height: 44px; /* Comfortable input target */
        }
        .layout-signin .row-col .ant-form-item { margin-bottom: 14px; }
        @media (min-width: 768px) {
          .layout-signin .ant-btn,
          .layout-signin .ant-btn-primary,
          .layout-signin .ant-btn-default {
            min-height: 48px; /* Scale up on tablets/desktop */
            padding-block: 14px;
            padding-inline: 20px;
          }
          .layout-signin .ant-input,
          .layout-signin .ant-input-password,
          .layout-signin .ant-input-affix-wrapper { min-height: 48px; }
          .layout-signin .row-col .ant-form-item { margin-bottom: 18px; }
        }

        /* --- Safe-area padding & robust viewport sizing --- */
        .layout-signin .signin {
          /* Preserve existing horizontal padding (0) while accounting for notches */
          padding: calc(24px + env(safe-area-inset-top)) 0 calc(8px + env(safe-area-inset-bottom));
          min-height: 100svh; /* Prefer dynamic viewport for mobile browsers */
        }
        @supports not (min-height: 100svh) {
          .layout-signin .signin { min-height: 100vh; }
        }

        /* --- Desktop trim: reduce vertical padding and allow content-driven height --- */
        @media (min-width: 992px) {
          .layout-signin .signin {
            min-height: auto;           /* do not force full viewport height on desktop */
            padding-top: 12px;          /* tighter top padding */
            padding-bottom: 12px;       /* tighter bottom padding */
          }
        }
        @media (min-width: 1200px) {
          .layout-signin .signin {      /* even tighter on very wide screens */
            padding-top: 8px;
            padding-bottom: 8px;
          }
        }

        /* --- Short-viewport rules (e.g., landscape phones, small laptops) --- */
        @media (max-height: 700px) {
          .signin-card { padding: 16px; }
          .layout-signin .signin-card .gradient-head { font-size: var(--step-3); }
          .layout-signin .signin {
            padding-top: calc(12px + env(safe-area-inset-top));
            padding-bottom: calc(6px + env(safe-area-inset-bottom));
          }
          .layout-signin .row-col .ant-form-item { margin-bottom: 12px; }
        }
        @media (max-height: 580px) {
          /* Prioritise the form on ultra-short viewports */
          .layout-signin .sign-img { display: none; }
        }

        /* === Luxe colour upgrade (overrides) === */
        .layout-signin {
          --lux-surface-1: #0b1220;
          --lux-surface-2: #0e1424;
          --lux-primary-1: #60a5fa;  /* sky */
          --lux-primary-2: #2563eb;  /* royal */
          --lux-accent-1: #22d3ee;   /* cyan */
          --lux-accent-2: #8b5cf6;   /* purple */
        }

        /* Richer ambient background */
        .layout-signin .signin {
          background:
            radial-gradient(1000px 480px at 12% 18%, rgba(201,171,99,0.12), transparent 62%),
            radial-gradient(1200px 700px at -10% 0%, rgba(201,171,99,0.06), transparent 70%),
            radial-gradient(1000px 600px at 50% 100%, rgba(124,212,255,0.06), transparent 70%);
        }

        /* Deeper glass card with brighter rim */
        .signin-card {
          background:
            radial-gradient(100% 80% at 0% 0%, rgba(201,171,99,0.22), transparent 60%),
            radial-gradient(110% 70% at 100% 100%, rgba(176,139,211,0.20), transparent 60%),
            linear-gradient(180deg, rgba(11,18,32,0.78), rgba(12,16,24,0.78));
          border-color: rgba(201,171,99,0.28);
          box-shadow: 0 18px 44px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.05);
        }
        .signin-card::after { opacity: 1; }
        .signin-card:hover { filter: saturate(1.10) brightness(1.04); }
        .signin-card .gradient-head {
          color: #F2E7C6;
          text-shadow: 0 2px 14px rgba(0,0,0,0.45), 0 0 1px rgba(0,0,0,0.60);
        }

        /* Elevated inputs with subtle inner glow */
        .layout-signin .ant-input,
        .layout-signin .ant-input-password,
        .layout-signin .ant-input-affix-wrapper {
          background: linear-gradient(180deg, rgba(14,20,36,0.78), rgba(11,17,30,0.78));
          border-color: rgba(147,197,253,0.30);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
        }
        .layout-signin .ant-input-affix-wrapper:hover,
        .layout-signin .ant-input-affix-wrapper-focused,
        .layout-signin .ant-input:focus,
        .layout-signin .ant-input-focused,
        .layout-signin .ant-input-password:focus-within {
          border-color: rgba(99,102,241,0.70) !important;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.28), 0 12px 26px rgba(59,130,246,0.16);
        }
        .layout-signin .ant-input-prefix,
        .layout-signin .ant-input-suffix { color: #c3d8ff; }

        /* Lux primary button */
        .layout-signin .ant-btn-primary {
          border: 1px solid rgba(96,165,250,0.98);
          background: linear-gradient(135deg, var(--lux-primary-1), var(--lux-primary-2) 55%, var(--lux-accent-1));
          box-shadow: 0 16px 34px rgba(37,99,235,0.42), 0 1px 0 rgba(255,255,255,0.06) inset;
          text-shadow: 0 1px 0 rgba(0,0,0,0.35);
          letter-spacing: 0.3px;
        }
        .layout-signin .ant-btn-primary:hover {
          filter: saturate(1.12) brightness(1.06);
          box-shadow: 0 20px 40px rgba(37,99,235,0.46), 0 1px 0 rgba(255,255,255,0.08) inset;
        }

        /* Enrich the hero image glaze for more depth */
        .layout-signin .sign-img::after {
          content: "";
          position: absolute; inset: 0; pointer-events: none;
          background:
            radial-gradient(120% 60% at 20% 20%, rgba(201,171,99,0.18), transparent 60%),
            radial-gradient(120% 60% at 100% 0%, rgba(176,139,211,0.18), transparent 60%);
        }

        /* Motion safety */
        @media (prefers-reduced-motion: reduce) {
          .signin-card::after { animation: none; }
        }

        @media (max-width: 991px) {
          .layout-signin .signin .ant-row { align-items: flex-start; }
          .layout-signin .sign-img { display: block; }
          .layout-signin .sign-img img { height: auto; max-height: none; }
        }
      `}</style>
    </div>
  );
};

export default SignIn;
